<script setup>
  import {inject, ref} from "vue";

  import ComponentEmptyState from "@components/EmptyState/EmptyState.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {Link as InertiaLink, router, useForm} from "@inertiajs/vue3";
  import {MedButton, MedLink, MedSpinner, MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "AuthHeaderNotificationsSlideover",
  });

  const privacy = inject("$privacy");

  const isOpen = ref(false);
  const isLoading = ref(true);

  const notifications = ref([]);
  const form = useForm({});

  function open() {
    isLoading.value = true;

    router.reload({
      only: ["allNotifications"],
      onSuccess: async (page) => {
        notifications.value = await Promise.all(
          (page.props.allNotifications?.data ?? [])
            .filter((notification) => {
              if (!notification.text && !notification.label) {
                console.error("Invalid notification: Missing text or label", notification);
                return false;
              }

              return true;
            })
            .map(async (notification) => {
              if (!notification.text || !notification.patient) {
                return notification;
              }

              const decryptedPatient = await privacy.decryptPatient(notification.patient);
              let decryptedText = notification.text.replaceAll(/%(\w+)%/g, (_, key) => {
                return decryptedPatient[key] ?? `%${key}%`;
              });

              return {...notification, text: decryptedText};
            }),
        );
        isLoading.value = false;
      },
    });

    isOpen.value = true;
  }

  function deleteNotification(id) {
    form.delete(route("notifications.destroy", {id: id}), {
      preserveScroll: true,
      onSuccess: () => {
        // just remove item from list; do not reload from server
        notifications.value = notifications.value.filter((notification) => notification.id !== id);
      },
    });
  }

  defineExpose({
    open,
  });
</script>

<template>
  <template v-if="isOpen">
    <section
      class="relative z-10 duration-500 ease-in-out"
      aria-labelledby="slide-over-title"
      role="dialog"
      aria-modal="true"
      :data-open="isOpen ? 'true' : 'false'"
      :data-test="getTestId('notifications-slide-over')"
    >
      <div class="fixed inset-0 bg-black/50 transition-opacity" />

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
            <div
              class="pointer-events-auto w-screen max-w-md"
              :data-test="getTestId('notifications-slide-over-container')"
            >
              <div class="flex h-full flex-col overflow-y-auto overscroll-contain bg-white py-6 shadow-xl">
                <div class="px-4 sm:px-6">
                  <div class="flex items-start justify-between">
                    <h2 id="slide-over-title" class="text-2xl leading-6 font-semibold text-gray-900">
                      Benachrichtigungen
                    </h2>

                    <div class="ml-3 flex h-7 items-center">
                      <MedButton
                        aria-label="Benachrichtigungen schließen"
                        variant="ghost"
                        data-test="auth-header-notification-button"
                        icon="close"
                        size="lg"
                        rounded="full"
                        @click="isOpen = false"
                      />
                    </div>
                  </div>
                </div>

                <div class="relative mt-6 w-full flex-1">
                  <template v-if="isLoading">
                    <div class="flex flex-col items-center">
                      <MedSpinner size="3xl" :data-test="getTestId('notifications-slide-over-spinner')" />
                    </div>
                  </template>

                  <ol v-else-if="notifications.length > 0" class="flex-1 divide-y divide-gray-300 overscroll-contain">
                    <li
                      v-for="(notification, index) in notifications"
                      :key="notification.id"
                      class="space-x-2 p-4 hover:bg-gray-100"
                    >
                      <div class="flex">
                        <MedTooltip content="Als gelesen markieren und ausblenden">
                          <button
                            class="group flex size-8 items-center justify-center rounded-full bg-gray-400 hover:bg-mcgreen-400 enabled:cursor-pointer"
                            :data-test="getTestId(`notifications-slide-over-notification-delete-button-${index}`)"
                            @click="deleteNotification(notification.id)"
                          >
                            <template v-if="notification.icon?.startsWith('custom:')">
                              <ComponentIcon
                                class="text-xl text-white group-hover:hidden"
                                :custom="notification.icon"
                                :small-custom-icon="false"
                              />
                            </template>

                            <template v-else>
                              <ComponentIcon class="text-xl text-white group-hover:hidden">
                                {{ notification.icon ?? "mail_outline" }}
                              </ComponentIcon>
                            </template>

                            <ComponentIcon class="hidden text-xl text-white group-hover:block">check</ComponentIcon>
                          </button>
                        </MedTooltip>

                        <component
                          :is="notification.url ? InertiaLink : 'div'"
                          :class="
                            cn('ml-2 flex-1 text-gray-900 hover:no-underline', {
                              'cursor-pointer': notification.url,
                            })
                          "
                          :href="notification.url ?? false"
                          :data-test="
                            getTestId(
                              `notifications-slide-over-notification-${notification.url ? 'inertia-link-' : ''}${index}`,
                            )
                          "
                          @click="isOpen = !notification.url"
                        >
                          <div class="flex items-baseline justify-between space-x-4">
                            <div
                              v-if="notification.label"
                              class="text-base font-semibold"
                              :class="{'text-mcred hover:underline': notification.url}"
                            >
                              {{ notification.label }}
                            </div>

                            <div
                              v-if="notification.created_at"
                              class="text-right text-sm whitespace-nowrap text-gray-500"
                            >
                              {{ notification.created_at }}
                            </div>
                          </div>

                          <div v-if="notification.text" class="text-sm">{{ notification.text }}</div>
                        </component>
                      </div>

                      <div class="text-right text-xs">
                        <MedLink
                          class="text-xs"
                          variant="primary"
                          :data-test="getTestId(`notifications-slide-over-notification-delete-${index}-link`)"
                          @click="deleteNotification(notification.id)"
                        >
                          als gelesen markieren
                        </MedLink>
                      </div>
                    </li>
                  </ol>

                  <ComponentEmptyState v-else :test-id="getTestId('notifications-slide-over')">
                    Sie haben zur Zeit keine Benachrichtigungen.
                  </ComponentEmptyState>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
</template>
